/* Atlas V3 Desktop — topbar mover ribbon.
   Port of the V2 desktop ribbon's smoothness contract (src/v2/components/
   MoverRibbon.jsx), restyled to v3 tokens:
     1. The animation element must NEVER remount on a price tick — the mover
        list recomputes only every 5 min via refs, so the scroll never snaps.
     2. Seamless -50% loop needs spacing ON THE ITEM (paddingRight), not a
        container gap — with gap, the loop undershoots and the seam jumps.
     3. Constant scroll speed: duration scales with item count (~40 px/s).
     4. will-change: transform for GPU compositing.
   Shows the top movers of the live universe (|1D %| desc), tap → dock.
   Prices come from the shared UniversePriceProvider (one fetch app-wide). */
import { useEffect, useRef, useState } from 'react'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useUniversePrices } from '@/v2/hooks/useUniversePrices'
import { useTicker } from '@/v2/v3/lib'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const ITEM_GAP_PX = 22
const ITEM_WIDTH_PX = 95
const PX_PER_S = 40
const REFRESH_MS = 5 * 60_000
const MAX_ITEMS = 30

function buildMovers(stocks, prices) {
  const out = stocks
    .map((s) => {
      const info = prices[s.ticker]
      if (!info?.price || info.change1d == null) return null
      return { ticker: s.ticker, change: info.change1d }
    })
    .filter(Boolean)
  out.sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
  return out.slice(0, MAX_ITEMS)
}

export function MoverRibbon() {
  const { stocks } = useAtlasStocks()
  const { prices } = useUniversePrices()
  const openTicker = useTicker()

  const pricesRef = useRef(prices)
  useEffect(() => { pricesRef.current = prices }, [prices])

  const [movers, setMovers] = useState([])
  const initialisedRef = useRef(false)

  // Phase 1: initial populate — wait for the first non-empty price batch,
  // then freeze so the animation div stays stable.
  useEffect(() => {
    if (initialisedRef.current) return
    const enriched = buildMovers(stocks, prices)
    if (enriched.length === 0) return
    setMovers(enriched)
    initialisedRef.current = true
  }, [prices, stocks])

  // Phase 2: refresh every 5 min via the ref — no dependency on prices, so
  // ticks never remount the animation element.
  useEffect(() => {
    const id = setInterval(() => {
      if (!initialisedRef.current) return
      const enriched = buildMovers(stocks, pricesRef.current)
      if (enriched.length) setMovers(enriched)
    }, REFRESH_MS)
    return () => clearInterval(id)
  }, [stocks])

  if (movers.length === 0) return null

  const loopSeconds = Math.max(30, Math.round((movers.length * ITEM_WIDTH_PX) / PX_PER_S))

  return (
    <div className="v3web-ribbon v3web-enter" style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-text-subtle))', flexShrink: 0, paddingRight: 12 }}>MOVERS</span>
      <div
        className="v3web-ribbon-track"
        style={{ animationDuration: `${loopSeconds}s`, willChange: 'transform', backfaceVisibility: 'hidden' }}
      >
        {[...movers, ...movers].map((m, i) => {
          const up = m.change >= 0
          return (
            <button
              key={`${i}-${m.ticker}`}
              type="button"
              onClick={() => openTicker && openTicker(m.ticker)}
              className="v3web-tk"
              style={{ all: 'unset', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, cursor: 'pointer', padding: `2px ${ITEM_GAP_PX}px 2px 4px`, borderRadius: 4 }}
            >
              <span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text))', letterSpacing: '0.03em' }}>{m.ticker}</span>
              <span style={{ ...Mono, fontSize: 10.5, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${up ? '--studio-success' : '--studio-danger'}))` }}>
                {up ? '▲' : '▼'} {Math.abs(m.change).toFixed(2)}%
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
